import { useEffect } from "react";

import { buildCurrentControlDeviceRegistration } from "../deviceRegistryIdentity";
import { cmmdDeviceRegistryResource } from "../cmmd/cmmdDeviceRegistryResource";
import { cmmdApiClient } from "../environments/cmmdApi";
import { isCloudBearerAuthFailure } from "../lib/cloudAuthFailure";
import { captureWebError } from "../sentry";

/**
 * Report a device-registration failure to Sentry, unless it is a CMMD
 * data-plane auth failure (HTTP 401 → CmmdApiError { kind: "auth" }).
 *
 * A CMMD 401 here is NOT a Forge error: it means the user simply hasn't
 * connected CMMD (or their delegated token lapsed and could not refresh). It is
 * a recoverable "reconnect CMMD" state that the transport's `onAuthExpired`
 * handler already surfaces via `markCmmdReconnectRequired`. Capturing it would
 * flood Sentry with one expected-state issue per user/deploy, matching the
 * quiet handling `useCmmdSync` and `cmmdForgeRuntimeListResource` already use
 * for the same failure.
 */
function reportDeviceRegistrationError(error: unknown, context: Record<string, string>): void {
  if (isCloudBearerAuthFailure(error)) return;
  captureWebError(error, context);
}

export function AuthenticatedDeviceRegistrationBootstrap() {
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const registration = await buildCurrentControlDeviceRegistration();
      if (cancelled) return;

      const registrationRegistry = await cmmdApiClient.registerDevice(registration);
      if (cancelled) return;
      cmmdDeviceRegistryResource.publish(registrationRegistry);
      void cmmdDeviceRegistryResource.refresh().catch((error: unknown) => {
        reportDeviceRegistrationError(error, {
          route: "authenticated-device-registration-bootstrap",
          phase: "device-registry-refresh",
        });
      });
    })().catch((error: unknown) => {
      reportDeviceRegistrationError(error, {
        route: "authenticated-device-registration-bootstrap",
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
